"use client";

import { useCallback, useEffect, useState } from "react";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import {
  type CombinedTransactionSummary,
  EMPTY_TRANSACTION_SUMMARY,
  fetchCombinedWorkspaceTransactionSummary,
} from "@/lib/transactionSummary";

type RefetchOptions = {
  silent?: boolean;
};

const EMPTY_COMBINED_TRANSACTION_SUMMARY: CombinedTransactionSummary = {
  income: EMPTY_TRANSACTION_SUMMARY,
  expense: EMPTY_TRANSACTION_SUMMARY,
  totalCount: 0,
};

export function useWorkspaceTransactionSummary(workspaceId?: string | null) {
  const [summary, setSummary] = useState<CombinedTransactionSummary | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(() =>
    Boolean(workspaceId?.trim()),
  );
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      const ws = workspaceId?.trim() ?? "";
      if (!ws) {
        setSummary(EMPTY_COMBINED_TRANSACTION_SUMMARY);
        setError(null);
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;

      try {
        if (!silent) setIsLoading(true);
        setError(null);
        const next = await fetchCombinedWorkspaceTransactionSummary(ws);
        setSummary(next);
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
        setSummary((prev) => prev ?? EMPTY_COMBINED_TRANSACTION_SUMMARY);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useWorkspaceActivityAutoRefresh(workspaceId, () =>
    refetch({ silent: true }),
  );

  return {
    summary: summary ?? EMPTY_COMBINED_TRANSACTION_SUMMARY,
    isLoading,
    error,
    refetch,
  };
}
